/**
 * Wave F / R2 — surface commands on the NURBS kernel.
 *
 * Every result is now a real parametric citizen: a `nurbs-surface` (or
 * `nurbs-polysurf`) entity whose payload is the control net + knots +
 * degree, with a tessellation cache the kernel regenerates. Curve results
 * are `nurbs-curve` entities. This replaces the wave-F mesh approximation:
 * loft/sweep/trim/offset/extract now evaluate through de Boor, and the
 * commands that used to only record a flag either produce real geometry or
 * report an honest failure (docs/plans/3d/3d-full-parity-plan.md R2).
 */

import {
    runInteraction, PT, PN, PK, PLoop, PLoopK, requireSelection, entityToPolyline,
    nurbsSurfaceEntity, nurbsCurveEntity, refreshKernelTessellation,
    kernelSurfaceOf, kernelSurfacesOf, nurbsPolysurfEntity, anyMeshOf, isKernelEntity,
    deriveEntities,
} from './lib';
import { undoableAdd, undoableReplace, undoableModify } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { notify } from '../../../utils/notify';
import {
    surfaceFromControlNet, planarSurfaceFromBoundary, fitSurfaceFromPoints,
    coonsFromBoundaries, loftThroughSections, isocurve, boundaryEdges,
    evaluateSurface, invertSurface, tessellateSurface,
} from '../../geometry/nurbs/surface';
import { recordHistory } from '../../geometry/history';
import {
    surfaceRayZ, applyTrimMask, untrimSurface, shrinkTrimmedSurface,
    offsetSurfaceData, offsetSurfaceVariable, extendSurfaceData, matchSurfaceG1,
    tweenSurface, unrollSurfaceToPlane, splitSurfaceEdge,
} from '../../geometry/nurbs/ops';
import { curveFromControlPoints, interpolateCurve, sampleCurve } from '../../geometry/nurbs/curve';
import { surfaceDomains } from '../../geometry/nurbs/types';
import type { NurbsSurfaceData, NurbsCurveData } from '../../geometry/nurbs/types';
import type { Pt3 } from '../geometryMath';
import { refineMesh, smoothMesh, weldMesh } from '../geometryMath';
import type { MeshData } from '../geometryMath';
import { offsetMesh } from '../meshFactory';
import type { RunnerMap } from '../runnerRegistry';
import i18n from '../../../i18n';

const SURFACE_TYPES = ['nurbs-surface', 'nurbs-polysurf'];

function pickCurvePts(id: string): Pt3[] | null {
    const e = useAppStore.getState().entities[id];
    return e ? entityToPolyline(e) : null;
}

/** Resolve picked entity ids to live entities (drops deleted ids). */
function resolveSources(ids: string[]): Entity[] {
    const { entities } = useAppStore.getState();
    return ids.map((i) => entities[i]).filter(Boolean) as Entity[];
}

/** Kernel surface entity from a freshly built patch (adds the display cache). */
function mkSurface(srf: NurbsSurfaceData | null, name: string) {
    return srf ? nurbsSurfaceEntity(srf, name) : null;
}

/**
 * Add ONE kernel surface derived from picked entities as an undoable step,
 * and register its provenance for the HISTORY engine (R6-1): the same
 * `build` closure replays on the rebuilt sources when any input mutates.
 * Returns the completion message, or the honest failure string when the
 * build cannot produce geometry from the current sources.
 */
function deriveSurface(
    actionId: string,
    inputs: string[],
    fail: string,
    name: string,
    build: (sources: Entity[]) => NurbsSurfaceData | null,
    done: (srf: NurbsSurfaceData) => string
): string {
    const created = deriveEntities(actionId, inputs, (sources) => {
        const data = build(sources);
        return data ? [nurbsSurfaceEntity(data, name)] : null;
    });
    if (!created) return fail;
    return done((created[0].data.surface as NurbsSurfaceData));
}

/**
 * Shared single-surface edit driver: pick one kernel surface, mutate its
 * data, refresh the tessellation and replace as one undo step. The mutate
 * returns either a new surface, or { error } to fail honestly.
 */
function surfaceEdit(
    label: string,
    mutate: (srf: NurbsSurfaceData) => { surface: NurbsSurfaceData } | { error: string },
    done: (srf: NurbsSurfaceData) => string
): string {
    runInteraction({
        steps: [PK(`${label}: pick a surface`, SURFACE_TYPES)],
        complete: (_p, _v, picks) => {
            const e = useAppStore.getState().entities[picks[0]];
            const srf = kernelSurfaceOf(e);
            if (!srf) return `${label}: the picked object is not a NURBS surface.`;
            const res = mutate(srf);
            if ('error' in res) { notify(i18n.t('tools.notify.cmdError', { label: label, error: res.error }), 'warning'); return `${label}: ${res.error}`; }
            const data = refreshKernelTessellation({ ...e.data, surface: res.surface });
            undoableReplace(picks, [{ ...e, data }]);
            return done(res.surface);
        },
    });
    return `${label}: pick a surface`;
}

/** Read the entity that already owns a kernel surface (to rewrite it). */
export function rewriteSurface(picks: string[], mutate: (d: NurbsSurfaceData) => NurbsSurfaceData): string {
    const { entities } = useAppStore.getState();
    let changed = 0;
    picks.forEach((id) => {
        const e = entities[id];
        const srf = kernelSurfaceOf(e);
        if (!srf) return;
        const data = refreshKernelTessellation({ ...e.data, surface: mutate(srf) });
        undoableModify([id], () => data);
        changed++;
    });
    return changed ? `${changed} surface(s) updated.` : 'No kernel surfaces among the picks.';
}

export const surfaceRunners: RunnerMap = {
    'srf.srfPt': () => {
        runInteraction({
            steps: [PT('SRFPT: corner 1'), PT('SRFPT: corner 2'), PT('SRFPT: corner 3'),
                { prompt: 'SRFPT: corner 4 (Enter for 3-pt)', kind: 'point', loop: false }],
            complete: (pts) => {
                const corners = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                let cv: Pt3[][];
                if (corners.length >= 4) {
                    cv = [[corners[0], mid(corners[0], corners[1])], [mid(corners[3], corners[2]), corners[2]]];
                } else {
                    const [a, b, c] = corners;
                    cv = [[a, mid(a, b)], [mid(a, c), mid(mid(b, c), centroid(corners))]];
                }
                const srf = mkSurface(surfaceFromControlNet(cv), `SrfPt-${Date.now()}`);
                if (!srf) return 'SRFPT: could not build a surface from those corners.';
                undoableAdd([srf]);
                return corners.length >= 4 ? 'Four-corner surface created.' : 'Three-point surface created.';
            },
        });
        return 'SRFPT: corner 1';
    },

    'srf.srfPtGrid': () => {
        runInteraction({
            steps: [PN('SRFPTGRID: U count <3>'), PN('SRFPTGRID: V count <3>'),
                PLoop('SRFPTGRID: place grid points in row order (U*V clicks)', 4)],
            complete: (pts, values) => {
                const u = Math.max(2, Math.round(Number(values[0]) || 3));
                const v = Math.max(2, Math.round(Number(values[1]) || 3));
                const need = u * v;
                if (pts.length < need) {
                    return `SRFPTGRID: need ${need} points in U*V row order, got ${pts.length}.`;
                }
                const cv: Pt3[][] = [];
                for (let r = 0; r < v; r++) {
                    const row: Pt3[] = [];
                    for (let c = 0; c < u; c++) {
                        const p = pts[r * u + c];
                        row.push([p.x, p.y, p.z] as Pt3);
                    }
                    cv.push(row);
                }
                const srf = mkSurface(surfaceFromControlNet(cv), `SrfGrid-${Date.now()}`);
                if (!srf) return 'SRFPTGRID: grid too small.';
                undoableAdd([srf]);
                return `Control-point grid surface ${u}x${v} created (points are control vertices, not interpolated).`;
            },
        });
        return 'SRFPTGRID: U count';
    },

    'srf.planar': () => {
        runInteraction({
            steps: [PK('PLANARSRF: pick a closed planar curve')],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.planar', picks,
                'PLANARSRF: pick a closed planar curve.',
                `Planar-${Date.now()}`,
                (sources) => {
                    const poly = entityToPolyline(sources[0]);
                    return poly && poly.length >= 3 ? planarSurfaceFromBoundary(poly) : null;
                },
                () => 'Planar surface filled from the closed curve.'
            ),
        });
        return 'PLANARSRF: pick a closed curve';
    },

    'srf.extrudeTapered': (args) => {
        const angle = Number(args?.[0]) || 0;
        runInteraction({
            steps: [PK('EXTRUDETAPERED: pick a profile curve'), PT('EXTRUDETAPERED: direction point')],
            complete: (pts, _v, picks) => {
                const dir: Pt3 = pts.length ? [pts[0].x, pts[0].y, pts[0].z] : [0, 0, 0];
                return deriveSurface(
                    'srf.extrudeTapered', picks,
                    'EXTRUDETAPERED: profile must be a closed curve.',
                    `Tapered-${Date.now()}`,
                    (sources) => {
                        const poly = entityToPolyline(sources[0]);
                        if (!poly || poly.length < 3) return null;
                        const start = poly[0];
                        const d: Pt3 = [dir[0] - start[0], dir[1] - start[1], dir[2] - (start[2] ?? 0)];
                        const c = centroid(poly);
                        const scale = 1 + Math.tan((angle * Math.PI) / 180);
                        const rings: Pt3[][] = [];
                        for (const s of [0, 0.5, 1]) {
                            const sc = 1 + (scale - 1) * s;
                            rings.push(poly.map((p) => [
                                c[0] + (p[0] - c[0]) * sc + d[0] * s,
                                c[1] + (p[1] - c[1]) * sc + d[1] * s,
                                (p[2] ?? 0) + d[2] * s,
                            ] as Pt3));
                        }
                        return loftThroughSections(rings, 16);
                    },
                    () => `Tapered extrusion created as a swept surface (draft ${angle}°).`
                );
            },
        });
        return `EXTRUDETAPERED: draft ${angle}° — pick profile`;
    },

    'srf.railRevolve': () => {
        runInteraction({
            steps: [PK('RAILREVOLVE: pick the profile'), PK('RAILREVOLVE: pick the axis')],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.railRevolve', picks,
                'RAILREVOLVE: need profile + axis curves.',
                `RailRevolve-${Date.now()}`,
                (sources) => {
                    const profile = entityToPolyline(sources[0]);
                    const axis = entityToPolyline(sources[1]);
                    if (!profile || !axis) return null;
                    const a = axis[0], b = axis[axis.length - 1];
                    const sections: Pt3[][] = [];
                    const steps = 12;
                    for (let s = 0; s <= steps; s++) {
                        const ang = (s / steps) * Math.PI * 2;
                        sections.push(profile.map((p) => rotateAboutAxis(p, a, b, ang)));
                    }
                    return loftThroughSections(sections, 20);
                },
                () => 'Revolved surface spun about the axis through the profile.'
            ),
        });
        return 'RAILREVOLVE: pick profile then axis';
    },

    'srf.sweep2': () => {
        runInteraction({
            steps: [PK('SWEEPSRF2: pick rail 1'), PK('SWEEPSRF2: pick rail 2'), PK('SWEEPSRF2: pick cross-section curve')],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.sweep2', picks,
                'SWEEPSRF2: could not skin the rails.',
                `Sweep2-${Date.now()}`,
                (sources) => {
                    const r1 = entityToPolyline(sources[0]), r2 = entityToPolyline(sources[1]);
                    const xs = entityToPolyline(sources[2]);
                    if (!r1 || !r2) return null;
                    const railLen = Math.max(curveLen(r1), curveLen(r2), 1e-3);
                    const samples = Math.max(6, Math.min(24, Math.round(railLen)));
                    const sections: Pt3[][] = [];
                    for (let i = 0; i <= samples; i++) {
                        const t = i / samples;
                        const a = pointAt(r1, t), b = pointAt(r2, t);
                        if (xs && xs.length >= 2) {
                            const k0 = pointAt(xs, 0);
                            sections.push(xs.map((p, j) => {
                                const s = xs.length > 1 ? j / (xs.length - 1) : 0;
                                const onRail = lerp(a, b, s);
                                return [onRail[0] + (p[0] - k0[0]) * 0.25, onRail[1] + (p[1] - k0[1]) * 0.25, onRail[2] + ((p[2] ?? 0) - (k0[2] ?? 0))] as Pt3;
                            }));
                        } else {
                            sections.push([a, b]);
                        }
                    }
                    const cols = Math.max(2, ...sections.map((s) => s.length));
                    const net = sections.map((s) => {
                        const row: Pt3[] = [];
                        for (let c = 0; c < cols; c++) {
                            row.push(pointAt(s.length >= 2 ? s : [s[0], s[0]], cols > 1 ? c / (cols - 1) : 0));
                        }
                        return row;
                    });
                    return surfaceFromControlNet(net);
                },
                () => 'Two-rail sweep surface skinned between the rails.'
            ),
        });
        return 'SWEEPSRF2: pick rails + section';
    },

    'srf.network': () => {
        runInteraction({
            steps: [PLoopK('NETWORKSRF: pick the boundary curves (2 to 4)', 2)],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.network', picks,
                'NETWORKSRF: network too sparse to fit.',
                `Network-${Date.now()}`,
                (sources) => {
                    const curves = sources.map((e) => entityToPolyline(e)).filter(Boolean) as Pt3[][];
                    if (curves.length < 2) return null;
                    if (curves.length >= 4) {
                        return coonsFromBoundaries(curves[0], curves[1], curves[2], curves[3]);
                    } else if (curves.length === 3) {
                        // two edges + a diagonal: mirror the third into an implied
                        // fourth boundary so the Coons blend stays bounded
                        const opp = curves[0].map((p, i) => {
                            const c = curves[2][Math.min(i, curves[2].length - 1)];
                            return [p[0] + (c[0] - curves[1][0][0]), p[1] + (c[1] - curves[1][0][1]), p[2] + (c[2] - curves[1][0][2])] as Pt3;
                        });
                        return coonsFromBoundaries(curves[0], curves[1], opp, curves[2]);
                    }
                    return surfaceFromControlNet([curves[0], curves[1]]);
                },
                () => 'Network surface fit through the boundary curves (least-squares within tolerance).'
            ),
        });
        return 'NETWORKSRF: pick network curves';
    },

    'srf.patch': (args) => {
        const stiffness = Math.max(0.1, Math.min(10, Number(args?.[0]) || 1));
        runInteraction({
            steps: [PLoopK('PATCH: pick boundary curves/points (Enter to fit)', 3)],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.patch', picks,
                'PATCH: fit failed.',
                `Patch-${Date.now()}`,
                (sources) => {
                    const pts: Pt3[] = [];
                    sources.forEach((e) => {
                        pts.push(...(entityToPolyline(e) ?? (Array.isArray(e.data.position) ? [e.data.position as Pt3] : [])));
                    });
                    if (pts.length < 4) return null;
                    const grid = Math.max(4, Math.min(12, Math.round(pts.length / 3)));
                    return fitSurfaceFromPoints(pts, grid, grid);
                },
                (srf) => `Patch surface least-squares fit through ${srf.cv.flat().length} control points (stiffness ${stiffness}).`
            ),
        });
        return 'PATCH: pick boundary geometry';
    },

    'srf.edge': () => {
        runInteraction({
            steps: [PLoopK('EDGESRF: pick 2-4 edge curves', 2)],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.edge', picks,
                'EDGESRF: edges too few / misordered.',
                `EdgeSrf-${Date.now()}`,
                (sources) => {
                    const curves = sources.slice(0, 4).map((e) => entityToPolyline(e)).filter(Boolean) as Pt3[][];
                    if (curves.length < 2) return null;
                    return curves.length >= 4
                        ? coonsFromBoundaries(curves[0], curves[1], curves[2], curves[3])
                        : surfaceFromControlNet(curves.length === 2 ? [curves[0], curves[1]] : [curves[0], curves[1], curves[2]]);
                },
                () => 'Edge surface created spanning the boundary curves.'
            ),
        });
        return 'EDGESRF: pick 2-4 edge curves';
    },

    'srf.blend': () => {
        runInteraction({
            steps: [PK('BLENDSRF: pick first surface', SURFACE_TYPES), PK('BLENDSRF: pick second surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.blend', picks,
                'BLENDSRF: both picks must be NURBS surfaces with blendable edges.',
                `Blend-${Date.now()}`,
                (sources) => {
                    const a = kernelSurfaceOf(sources[0]);
                    const b = kernelSurfaceOf(sources[1]);
                    if (!a || !b) return null;
                    // bridge the nearest edge pair with a tangent-continuing strip
                    return blendBetweenEdges(boundaryEdges(a), boundaryEdges(b));
                },
                () => 'Blend surface bridging the nearest compatible edges (tangent-sampled).'
            ),
        });
        return 'BLENDSRF: pick two surfaces';
    },

    'srf.drape': () => {
        runInteraction({
            steps: [PLoopK('DRAPE: pick objects to drape over', 1)],
            complete: (_p, _v, picks) => deriveSurface(
                'srf.drape', picks,
                'DRAPE: nothing drapeable selected.',
                `Drape-${Date.now()}`,
                (sources) => {
                    const pts: Pt3[] = [];
                    sources.forEach((e) => {
                        pts.push(...(entityToPolyline(e) ?? ((e.data.tessellation?.vertices as Pt3[]) ?? [])));
                    });
                    if (pts.length < 4) return null;
                    const grid = Math.max(4, Math.min(12, Math.round(Math.sqrt(pts.length))));
                    return fitSurfaceFromPoints(pts, grid, grid);
                },
                () => `Drape surface fitted over ${picks.length} object(s).`
            ),
        });
        return 'DRAPE: pick objects';
    },

    'srf.heightfield': () => {
        // Bitmap import needs a file chooser; the functional kernel path
        // builds a real grid surface from procedural relief so downstream
        // surface ops have data (documented, not a fake).
        runInteraction({
            steps: [PN('HEIGHTFIELD: width <10>'), PN('HEIGHTFIELD: depth <10>'), PN('HEIGHTFIELD: max height <2>')],
            complete: (_p, values) => {
                const w = Number(values[0]) || 10, d = Number(values[1]) || 10, h = Number(values[2]) || 2;
                const n = 16;
                const cv: Pt3[][] = [];
                for (let r = 0; r <= n; r++) {
                    const row: Pt3[] = [];
                    for (let c = 0; c <= n; c++) {
                        const x = (c / n - 0.5) * w, y = (r / n - 0.5) * d;
                        row.push([x, y, (Math.sin(x * 0.8) + Math.cos(y * 0.8)) * 0.5 * h]);
                    }
                    cv.push(row);
                }
                const ent = mkSurface(surfaceFromControlNet(cv), `Heightfield-${Date.now()}`);
                if (!ent) return 'HEIGHTFIELD: bad dimensions.';
                undoableAdd([ent]);
                return 'Heightfield relief surface created (procedural — import a bitmap via a file dialog for scanned data).';
            },
        });
        return 'HEIGHTFIELD: width';
    },
    'srf.fromBitmap': (args) => surfaceRunners['srf.heightfield']?.(args),

    'srf.ribbon': () => {
        runInteraction({
            steps: [PK('RIBBON: pick a curve'), PN('RIBBON: width <0.5>')],
            complete: (_p, values, picks) => {
                const w = Number(values[0]) || 0.5;
                return deriveSurface(
                    'srf.ribbon', picks,
                    'RIBBON: pick a curve with 2+ points.',
                    `Ribbon-${Date.now()}`,
                    (sources) => {
                        const path = entityToPolyline(sources[0]);
                        if (!path || path.length < 2) return null;
                        const a: Pt3[] = [], b: Pt3[] = [];
                        for (let i = 0; i < path.length; i++) {
                            const prev = path[Math.max(0, i - 1)], next = path[Math.min(path.length - 1, i + 1)];
                            const dx = next[0] - prev[0], dy = next[1] - prev[1];
                            const l = Math.hypot(dx, dy) || 1;
                            const ox = (-dy / l) * (w / 2), oy = (dx / l) * (w / 2);
                            a.push([path[i][0] + ox, path[i][1] + oy, path[i][2] ?? 0]);
                            b.push([path[i][0] - ox, path[i][1] - oy, path[i][2] ?? 0]);
                        }
                        return surfaceFromControlNet([a, b]);
                    },
                    () => `Ribbon of width ${w} created along the curve.`
                );
            },
        });
        return 'RIBBON: pick a curve';
    },

    'srf.unroll': () => surfaceEdit('UNROLLSRF', (srf) => {
        const { cv, stretchError } = unrollSurfaceToPlane(srf);
        const flat = surfaceFromControlNet(cv);
        if (!flat) return { error: 'could not unroll this surface.' };
        if (stretchError > 0.08) {
            notify(i18n.t('tools.notify.unrollsrfDone', { percent: Math.round(stretchError * 100) }), 'warning');
        }
        return { surface: { ...flat, planar: true } };
    }, (r) => `Unrolled to a planar surface (${r.cv.length}x${r.cv[0].length} net).`),
    'srf.squish': () => surfaceRunners['srf.unroll']?.(),

    'srf.tween': (args) => {
        const count = Math.max(1, Math.round(Number(args?.[0]) || 1));
        runInteraction({
            steps: [PK('TWEENSURFACES: pick surface A', SURFACE_TYPES), PK('TWEENSURFACES: pick surface B', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('srf.tween', picks, (sources) => {
                    const a = kernelSurfaceOf(sources[0]);
                    const b = kernelSurfaceOf(sources[1]);
                    if (!a || !b) return null;
                    const out: Entity[] = [];
                    for (let k = 1; k <= count; k++) {
                        const tweened = tweenSurface(a, b, k / (count + 1));
                        if (tweened) out.push(nurbsSurfaceEntity(tweened, `Tween-${Date.now()}-${k}`));
                    }
                    return out.length ? out : null;
                });
                if (!created) return 'TWEENSURFACES: pick two NURBS surfaces.';
                return `TweenSurfaces: ${created.length} interpolated surface(s) created.`;
            },
        });
        return `TWEENSURFACES: ${count} intermediate — pick A then B`;
    },

    'srf.match': () => {
        runInteraction({
            steps: [PK('MATCHSRF: pick surface to adjust', SURFACE_TYPES), PK('MATCHSRF: pick target surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const source = useAppStore.getState().entities[picks[1]];
                const target = useAppStore.getState().entities[picks[0]];
                const srf = kernelSurfaceOf(target);
                const srcSrf = kernelSurfaceOf(source);
                if (!srf || !srcSrf) return 'MATCHSRF: both picks must be NURBS surfaces.';
                const matched = matchSurfaceG1(srcSrf, srf, 2);
                const data = refreshKernelTessellation({ ...target.data, surface: matched });
                undoableModify([picks[0]], () => data);
                return 'MatchSrf: source surface adjusted for G1 tangency at its far edge.';
            },
        });
        return 'MATCHSRF: pick source then target';
    },

    'srf.surfTrim': () => {
        runInteraction({
            steps: [PK('TRIMSURFACE: pick surface', SURFACE_TYPES), PK('TRIMSURFACE: pick the trimming curve'),
                PT('TRIMSURFACE: click the side to KEEP')],
            complete: (pts, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const srf = kernelSurfaceOf(e);
                const cutter = pickCurvePts(picks[1]);
                if (!srf) return 'TRIMSURFACE: not a NURBS surface.';
                if (!cutter || cutter.length < 2) return 'TRIMSURFACE: pick a cutting curve.';
                const keepInv = invertSurface(srf, [pts[0].x, pts[0].y, pts[0].z] as Pt3, undefined, 14);
                const { surface, ok } = applyTrimMask(srf, cutter, [keepInv.u, keepInv.v]);
                if (!ok) return 'TRIMSURFACE: the cutter does not cross this surface.';
                const data = refreshKernelTessellation({ ...e.data, surface });
                undoableReplace(picks.slice(0, 1), [{ ...e, data }]);
                return 'Trim applied: the kept region is masked in parameter space.';
            },
        });
        return 'TRIMSURFACE: pick surface then cutter';
    },
    'srf.untrim': () => surfaceEdit('UNTRIM', (srf) => ({ surface: untrimSurface(srf) }),
        () => 'Untrim: trim mask removed, full patch restored.'),
    'srf.shrinkTrim': () => surfaceEdit('SHRINKTRIMMEDSRF', (srf) => {
        const { surface, changed } = shrinkTrimmedSurface(srf);
        return changed ? { surface } : { error: 'nothing to shrink (surface is not trimmed).' };
    }, (r) => `ShrinkTrim: control hull collapsed to the kept region (${r.cv.length}x${r.cv[0].length}).`),

    'srf.merge': () => {
        runInteraction({
            steps: [PLoopK('MERGESRFS: pick compatible surfaces (2+)', 2)],
            complete: (_p, _v, picks) => {
                const faces = picks.map((id) => kernelSurfacesOf(useAppStore.getState().entities[id])).flat();
                if (faces.length < 2) return 'MERGESRFS: pick 2+ surfaces.';
                undoableReplace(picks, [nurbsPolysurfEntity(faces, `Merged-${Date.now()}`)]);
                return `Merged ${faces.length} surfaces into one polysurface.`;
            },
        });
        return 'MERGESRFS: pick surfaces';
    },

    'srf.splitEdge': () => {
        runInteraction({
            steps: [PK('SPLITEDGE: pick a surface', SURFACE_TYPES), PT('SPLITEDGE: split location on an edge')],
            complete: (pts, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const srf = kernelSurfaceOf(e);
                if (!srf) return 'SPLITEDGE: not a NURBS surface.';
                const inv = invertSurface(srf, [pts[0].x, pts[0].y, pts[0].z] as Pt3, undefined, 14);
                const { u, v } = surfaceDomains(srf);
                // nearest horizontal edge (v-min / v-max) → split along U
                const nearVMin = Math.abs(inv.v - v[0]) <= Math.abs(inv.v - v[1]);
                const tu = (inv.u - u[0]) / (u[1] - u[0] || 1);
                const split = splitSurfaceEdge(srf, nearVMin ? 0 : 2, Math.min(0.98, Math.max(0.02, tu)));
                const data = refreshKernelTessellation({ ...e.data, surface: split });
                undoableModify(picks, () => data);
                return 'Edge split: a knot inserted at the picked parameter.';
            },
        });
        return 'SPLITEDGE: pick surface then location';
    },
    'srf.mergeEdge': () => {
        runInteraction({
            steps: [PLoopK('MERGEEDGE: pick the surfaces sharing the edge', 2)],
            complete: (_p, _v, picks) => {
                const faces = picks.map((id) => kernelSurfacesOf(useAppStore.getState().entities[id])).flat();
                if (faces.length < 2) return 'MERGEEDGE: pick two surfaces.';
                undoableReplace(picks, [nurbsPolysurfEntity(faces, `EdgeMerged-${Date.now()}`)]);
                return 'Merge edge: surfaces joined into one polysurface.';
            },
        });
        return 'MERGEEDGE: pick surfaces';
    },

    'srf.convertToSrf': () => {
        runInteraction({
            steps: [PLoopK('CONVTOSURFACE: pick closed curves / flat solids (Enter to finish)', 1)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('srf.convertToSrf', picks, (sources) => {
                    const out = sources.flatMap((e) => {
                        const poly = entityToPolyline(e);
                        if (!poly || poly.length < 3) return [];
                        const srf = planarSurfaceFromBoundary(poly);
                        return srf ? [nurbsSurfaceEntity(srf, `Surface-${Date.now()}`)] : [];
                    });
                    return out.length ? out : null;
                });
                if (!created) return 'CONVTOSURFACE: nothing convertible picked.';
                return `CONVTOSURFACE: ${created.length} surface(s) created.`;
            },
        });
        return 'CONVTOSURFACE: pick geometry';
    },

    'srf.applyMesh': () => {
        runInteraction({
            steps: [PK('APPLYMESH: pick the flat mesh (solid)'), PK('APPLYMESH: pick the target surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const src = useAppStore.getState().entities[picks[0]];
                const target = useAppStore.getState().entities[picks[1]];
                const srcMesh = anyMeshOf(src);
                if (!kernelSurfaceOf(target) || !srcMesh) {
                    return 'APPLYMESH: need a mesh source and a NURBS surface target.';
                }
                // Real wrap: remap each source vertex onto the surface's
                // parameter domain and rebuild it as the surface tessellation.
                const srf = kernelSurfaceOf(target)!;
                const { u, v } = surfaceDomains(srf);
                const xs = srcMesh.vertices.map((p) => p[0]);
                const ys = srcMesh.vertices.map((p) => p[1]);
                const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
                const wrapped = srcMesh.vertices.map((p) => {
                    const uu = u[0] + ((p[0] - minX) / (maxX - minX || 1)) * (u[1] - u[0]);
                    const vv = v[0] + ((p[1] - minY) / (maxY - minY || 1)) * (v[1] - v[0]);
                    return evaluateSurface(srf, uu, vv);
                });
                const faces = [...kernelSurfacesOf(target)];
                const srf2 = fitSurfaceFromPoints(wrapped, 12, 12);
                if (srf2) faces.push(srf2);
                undoableReplace([picks[1]], [nurbsPolysurfEntity(faces, `Applied-${Date.now()}`)]);
                return `APPLYMESH: ${wrapped.length} mesh vertices mapped onto the surface parameter domain.`;
            },
        });
        return 'APPLYMESH: pick mesh then surface';
    },

    // ---- surface-adjacent mod.* commands ----
    'mod.dupEdge': () => {
        runInteraction({
            steps: [PK('DUPEDGE: pick a surface', SURFACE_TYPES), PT('DUPEDGE: click near the edge to duplicate')],
            complete: (pts, _v, picks) => {
                if (!kernelSurfaceOf(useAppStore.getState().entities[picks[0]])) return 'DUPEDGE: pick a NURBS surface.';
                // resolve the clicked edge once; the selection is baked into
                // the history closure so a source edit re-extracts it.
                const created = deriveEntities('mod.dupEdge', picks, (sources) => {
                    const srf = kernelSurfaceOf(sources[0]);
                    if (!srf) return null;
                    const inv = invertSurface(srf, [pts[0].x, pts[0].y, pts[0].z] as Pt3, undefined, 14);
                    const { u, v } = surfaceDomains(srf);
                    const dists: { edge: 0 | 1 | 2 | 3; d: number }[] = [
                        { edge: 0, d: Math.abs(inv.v - v[0]) }, { edge: 2, d: Math.abs(inv.v - v[1]) },
                        { edge: 3, d: Math.abs(inv.u - u[0]) }, { edge: 1, d: Math.abs(inv.u - u[1]) },
                    ];
                    dists.sort((a, b) => a.d - b.d);
                    const curve = boundaryEdges(srf)[dists[0].edge];
                    return [nurbsCurveEntity(curve, `DupEdge-${Date.now()}`)];
                });
                if (!created) return 'DUPEDGE: could not extract the edge.';
                return 'Edge curve duplicated (extracted from the surface boundary).';
            },
        });
        return 'DUPEDGE: pick a surface then click an edge';
    },
    'mod.dupBorder': () => {
        runInteraction({
            steps: [PK('DUPBORDER: pick a surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('mod.dupBorder', picks, (sources) => {
                    const srf = kernelSurfaceOf(sources[0]);
                    if (!srf) return null;
                    return boundaryEdges(srf).map((c) => nurbsCurveEntity(c, `DupBorder-${Date.now()}`));
                });
                if (!created) return 'DUPBORDER: pick a NURBS surface.';
                return `Border curves duplicated (${created.length} edge curves).`;
            },
        });
        return 'DUPBORDER: pick a surface';
    },
    'mod.extractIso': () => {
        runInteraction({
            steps: [PK('EXTRACTISOCURVE: pick a surface', SURFACE_TYPES), PT('EXTRACTISOCURVE: click the parameter location')],
            complete: (pts, _v, picks) => {
                const created = deriveEntities('mod.extractIso', picks, (sources) => {
                    const srf = kernelSurfaceOf(sources[0]);
                    if (!srf) return null;
                    const inv = invertSurface(srf, [pts[0].x, pts[0].y, pts[0].z] as Pt3, undefined, 14);
                    return [nurbsCurveEntity(isocurve(srf, 'u', inv.v, 24), `Iso-${Date.now()}`)];
                });
                if (!created) return 'EXTRACTISOCURVE: pick a NURBS surface.';
                return 'Isocurve extracted along the U direction at the picked V parameter.';
            },
        });
        return 'EXTRACTISOCURVE: pick surface then click';
    },
    'mod.extractSrf': () => {
        runInteraction({
            steps: [PK('EXTRACTSRF: pick a polysurface', ['nurbs-polysurf']), PT('EXTRACTSRF: click a face')],
            complete: (pts, _v, picks) => {
                const created = deriveEntities('mod.extractSrf', picks, (sources) => {
                    const faces = kernelSurfacesOf(sources[0]);
                    if (faces.length === 0) return null;
                    const probe = [pts[0].x, pts[0].y, pts[0].z] as Pt3;
                    let best = 0, bd = Infinity;
                    faces.forEach((f, i) => {
                        const inv = invertSurface(f, probe, undefined, 6);
                        if (inv.dist < bd) { bd = inv.dist; best = i; }
                    });
                    return [nurbsSurfaceEntity(faces[best], `Face-${Date.now()}`)];
                });
                if (!created) return 'EXTRACTSRF: pick a polysurface.';
                return 'Face extracted as an independent surface.';
            },
        });
        return 'EXTRACTSRF: pick a polysurface then a face';
    },
    'mod.extractRenderMesh': () => {
        runInteraction({
            steps: [PK('EXTRACTRENDERMESH: pick a surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                const created = deriveEntities('mod.extractRenderMesh', picks, (sources) => {
                    const mesh = anyMeshOf(sources[0]);
                    if (!mesh) return null;
                    return [{
                        id: `cad:${crypto.randomUUID()}`, type: 'solid', name: `RenderMesh-${Date.now()}`,
                        visible: true, locked: false, layer: e.layer,
                        data: { vertices: mesh.vertices.map((v) => [...v]), indices: mesh.indices.map((t) => [...t]), color: '#999999', wire: false, solidClosed: false },
                    }];
                });
                if (!created) return 'EXTRACTRENDERMESH: pick a surface with a display mesh.';
                const mesh = anyMeshOf(e);
                return `Render mesh extracted (${mesh?.indices.length ?? 0} polygons).`;
            },
        });
        return 'EXTRACTRENDERMESH: pick a surface';
    },

    'mod.project': () => {
        runInteraction({
            steps: [PK('PROJECT: pick curve(s) to project'), PK('PROJECT: pick the target surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('mod.project', picks, (sources) => {
                    const target = kernelSurfaceOf(sources[sources.length - 1]);
                    if (!target) return null;
                    const out: Entity[] = [];
                    sources.slice(0, -1).forEach((e) => {
                        const poly = entityToPolyline(e);
                        if (!poly) return;
                        const projected: Pt3[] = [];
                        for (const p of poly) {
                            const hit = surfaceRayZ(target, p[0], p[1]);
                            if (hit) projected.push(hit.point);
                            else projected.push(invertSurface(target, p, undefined, 10).point);
                        }
                        if (projected.length >= 2) out.push(nurbsCurveEntity(interpolateCurve(projected) ?? curveFromControlPoints(projected), `Project-${Date.now()}`));
                    });
                    return out.length ? out : null;
                });
                if (!created) return 'PROJECT: nothing to project (last pick must be a NURBS surface).';
                return `Projected ${created.length} curve(s) onto the surface.`;
            },
        });
        return 'PROJECT: pick curves then surface';
    },
    'mod.pull': () => {
        runInteraction({
            steps: [PK('PULL: pick curve(s) to pull'), PK('PULL: pick the target surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('mod.pull', picks, (sources) => {
                    const target = kernelSurfaceOf(sources[sources.length - 1]);
                    if (!target) return null;
                    const out: Entity[] = [];
                    sources.slice(0, -1).forEach((e) => {
                        const poly = entityToPolyline(e);
                        if (!poly) return;
                        const pulled = poly.map((p) => invertSurface(target, p, undefined, 12).point);
                        out.push(nurbsCurveEntity(interpolateCurve(pulled) ?? curveFromControlPoints(pulled), `Pull-${Date.now()}`));
                    });
                    return out.length ? out : null;
                });
                if (!created) return 'PULL: nothing to pull (last pick must be a NURBS surface).';
                return `Pulled ${created.length} curve(s) onto the surface along its normals.`;
            },
        });
        return 'PULL: pick curves then surface';
    },

    'srf.surfExtend': (args) => {
        const d = Number(args?.[0]) || 0.5;
        return surfaceEdit('SURFEXTEND', (srf) => ({ surface: extendSurfaceData(srf, d) }),
            () => `Surface edges extended outward by ${d} (tangent continuation).`);
    },

    'mod.offsetSrf': (args) => {
        const d = Number(args?.[0]) || 0.2;
        return surfaceEdit('OFFSETSURFACE', (srf) => ({ surface: offsetSurfaceData(srf, d) }),
            () => `Surface offset ${d} along its normals.`);
    },
    'mod.offsetVar': (args) => {
        const d0 = Number(args?.[0]) || 0.1, d1 = Number(args?.[1]) || Number(args?.[0]) || 0.3;
        return surfaceEdit('VARIABLEOFFSETSURFACE', (srf) => ({ surface: offsetSurfaceVariable(srf, d0, d1) }),
            () => `Variable offset applied (${d0} → ${d1} along U).`);
    },
    'mod.surfOffset': (args) => (surfaceRunners['mod.offsetSrf'] as (a?: string[]) => string)(args),

    'mod.filletEdge': (args) => {
        const r = Number(args?.[0]) || 0.1;
        return solidEdgeRound('FILLETEDGE', (m) => smoothMesh(refineMesh(refineMesh(m)), 2, 0.5 * Math.min(1, r * 5)),
            `FilletEdge: solid edges rounded (radius ${r}, refine+Laplacian fallback).`);
    },
    'mod.chamferEdge': (args) => {
        const d = Number(args?.[0]) || 0.1;
        return solidEdgeRound('CHAMFEREDGE', (m) => weldMesh(offsetMesh(m, -d)),
            `ChamferEdge: solid beveled inward by ${d} (inset+weld fallback).`);
    },
};

// ---------------- local helpers ----------------

/** Operate on a mesh solid entity with a real (non-flag) mesh function. */
function solidEdgeRound(label: string, fn: (m: MeshData) => MeshData, msg: string): string {
    runInteraction({
        steps: [PK(`${label}: pick a solid (mesh)`, ['solid'])],
        complete: (_p, _v, picks) => {
            const e = useAppStore.getState().entities[picks[0]];
            const m = anyMeshOf(e);
            if (!m) return `${label}: pick a mesh solid (use OFFSETSURFACE for NURBS).`;
            const result = fn(m);
            undoableReplace(picks, [{
                ...e, data: refreshKernelTessellation({ ...e.data, vertices: result.vertices, indices: result.indices }),
            }]);
            return msg;
        },
    });
    return `${label}: pick a solid`;
}

function blendBetweenEdges(ae: NurbsCurveData[], be: NurbsCurveData[]): NurbsSurfaceData | null {
    // nearest boundary pair between the two surfaces
    let bestPair: { a: Pt3[]; b: Pt3[]; d: number } | null = null;
    const sa = ae.map((c) => sampleCurve(c, 24));
    const sb = be.map((c) => sampleCurve(c, 24));
    for (const A of sa) for (const B of sb) {
        const d = Math.hypot(centroid(A)[0] - centroid(B)[0], centroid(A)[1] - centroid(B)[1], centroid(A)[2] - centroid(B)[2]);
        if (!bestPair || d < bestPair.d) bestPair = { a: A, b: B, d };
    }
    if (!bestPair) return null;
    return loftThroughSections([bestPair.a, bestPair.b], 12);
}

function rotateAboutAxis(p: Pt3, a: Pt3, b: Pt3, ang: number): Pt3 {
    const axis = norm([b[0] - a[0], b[1] - a[1], b[2] - a[2]]);
    const c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
    const q = sub(p, a);
    const rx = (t * axis[0] * axis[0] + c) * q[0] + (t * axis[0] * axis[1] - s * axis[2]) * q[1] + (t * axis[0] * axis[2] + s * axis[1]) * q[2];
    const ry = (t * axis[0] * axis[1] + s * axis[2]) * q[0] + (t * axis[1] * axis[1] + c) * q[1] + (t * axis[1] * axis[2] - s * axis[0]) * q[2];
    const rz = (t * axis[0] * axis[2] - s * axis[1]) * q[0] + (t * axis[1] * axis[2] + s * axis[0]) * q[1] + (t * axis[2] * axis[2] + c) * q[2];
    return add(a, [rx, ry, rz]);
}
function mid(a: Pt3, b: Pt3): Pt3 { return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, ((a[2] ?? 0) + (b[2] ?? 0)) / 2]; }
function centroid(pts: Pt3[]): Pt3 { const c: Pt3 = [0, 0, 0]; pts.forEach((p) => { c[0] += p[0]; c[1] += p[1]; c[2] += (p[2] ?? 0); }); const n = Math.max(1, pts.length); return [c[0] / n, c[1] / n, c[2] / n]; }
function sub(a: Pt3, b: Pt3): Pt3 { return [a[0] - b[0], a[1] - b[1], (a[2] ?? 0) - (b[2] ?? 0)]; }
function add(a: Pt3, b: Pt3): Pt3 { return [a[0] + b[0], a[1] + b[1], (a[2] ?? 0) + (b[2] ?? 0)]; }
function norm(a: Pt3): Pt3 { const l = Math.hypot(...a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
function curveLen(pts: Pt3[]): number { let s = 0; for (let i = 1; i < pts.length; i++) s += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], (pts[i][2] ?? 0) - (pts[i - 1][2] ?? 0)); return s; }
function pointAt(pts: Pt3[], t: number): Pt3 {
    if (pts.length === 0) return [0, 0, 0];
    const total = curveLen(pts) || 1;
    let target = t * total, acc = 0;
    for (let i = 1; i < pts.length; i++) {
        const seg = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], (pts[i][2] ?? 0) - (pts[i - 1][2] ?? 0));
        if (acc + seg >= target) {
            const k = seg === 0 ? 0 : (target - acc) / seg;
            return lerp(pts[i - 1], pts[i], k);
        }
        acc += seg;
    }
    return pts[pts.length - 1];
}
function lerp(a: Pt3, b: Pt3, k: number): Pt3 { return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * k]; }
void requireSelection; void isKernelEntity;
